<template>
  <form class="auth-step" @submit.prevent="$emit('submit')">
    <div class="auth-heading">
      <i class="bi bi-person-plus auth-brand-icon-placeholder"></i>
      <h3 class="auth-title">Create Account</h3>
      <p class="auth-subtitle">Join us and find your perfect stay</p>
    </div>

    <div v-if="errorMessage" class="auth-error">
      <i class="bi bi-exclamation-circle"></i>
      {{ errorMessage }}
    </div>

    <div class="form-inner two mb-20">
      <label><i class="bi bi-person me-1"></i>Full Name <span class="req">*</span></label>
      <input
        :value="signupDraft.fullName"
        type="text"
        placeholder="Enter full name"
        @input="updateField('fullName', $event.target.value)"
      >
      <div v-if="fieldErrors.fullName" class="field-error">{{ fieldErrors.fullName }}</div>
    </div>

    <div class="form-inner two mb-20">
      <label><i class="bi bi-phone me-1"></i>Mobile No. <span class="req">*</span></label>
      <div class="mobile-input-row">
        <span class="mobile-prefix">+91</span>
        <input
          :value="mobileDigits"
          type="tel"
          placeholder="Enter mobile number"
          maxlength="10"
          @input="onMobileInput"
        >
      </div>
      <div v-if="fieldErrors.mobile" class="field-error">{{ fieldErrors.mobile }}</div>
    </div>

    <div class="form-inner two mb-20">
      <label><i class="bi bi-envelope me-1"></i>Email <span class="req">*</span></label>
      <input
        :value="signupDraft.email"
        type="email"
        placeholder="Enter email address"
        autocomplete="email"
        @input="updateField('email', $event.target.value)"
      >
      <div v-if="fieldErrors.email" class="field-error">{{ fieldErrors.email }}</div>
      <div v-else class="field-hint">We'll send a verification code here so you receive booking updates.</div>
    </div>

    <div class="form-inner two mb-20">
      <label><i class="bi bi-lock me-1"></i>Password <span class="req">*</span></label>
      <div class="password-wrapper">
        <input
          :value="signupDraft.password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="Create a password"
          autocomplete="new-password"
          @input="updateField('password', $event.target.value)"
        >
        <button type="button" class="password-toggle" @click="showPassword = !showPassword">
          <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        </button>
      </div>
      <div v-if="signupDraft.password" class="password-strength-bar">
        <div class="strength-fill" :class="passwordStrengthClass" :style="{ width: passwordStrengthPercent + '%' }"></div>
      </div>
      <div v-if="signupDraft.password" class="password-strength-label" :class="passwordStrengthClass">{{ passwordStrengthLabel }}</div>
      <div class="password-hint">Min 8 characters, one uppercase letter, one special character.</div>
      <div v-if="fieldErrors.password" class="field-error">{{ fieldErrors.password }}</div>
    </div>

    <div class="form-inner two mb-20">
      <label><i class="bi bi-lock me-1"></i>Confirm Password <span class="req">*</span></label>
      <div class="password-wrapper">
        <input
          :value="signupDraft.passwordConfirmation"
          :type="showConfirmPassword ? 'text' : 'password'"
          placeholder="Confirm your password"
          autocomplete="new-password"
          @input="updateField('passwordConfirmation', $event.target.value)"
        >
        <button type="button" class="password-toggle" @click="showConfirmPassword = !showConfirmPassword">
          <i :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
        </button>
      </div>
      <div v-if="fieldErrors.passwordConfirmation" class="field-error">{{ fieldErrors.passwordConfirmation }}</div>
    </div>

    <button type="submit" class="primary-btn1 auth-submit-btn mb-20" :disabled="busy">
      <span>{{ busyText }}</span>
    </button>

    <p class="auth-switch">
      Already have an account?
      <button type="button" @click="$emit('switch-mode')">Sign In</button>
    </p>
  </form>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  signupDraft: {
    type: Object,
    required: true
  },
  fieldErrors: {
    type: Object,
    default: () => ({})
  },
  status: {
    type: String,
    default: 'idle'
  },
  errorMessage: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['submit', 'switch-mode', 'update:signupDraft'])

const busy = computed(() => ['signing_up', 'validating_mobile', 'sending_otp'].includes(props.status))
const busyText = computed(() => {
  if (props.status === 'validating_mobile') return 'Validating...'
  if (props.status === 'sending_otp') return 'Sending OTP...'
  if (props.status === 'signing_up') return 'Creating Account...'
  return 'Continue'
})
const showPassword = ref(false)
const showConfirmPassword = ref(false)

const passwordStrength = computed(() => {
  const pw = props.signupDraft.password || ''
  if (!pw) return 0
  let score = 0
  if (pw.length >= 8) score++
  if (/[A-Z]/.test(pw)) score++
  if (/[0-9]/.test(pw)) score++
  if (/[^A-Za-z0-9]/.test(pw)) score++
  return score
})

const passwordStrengthClass = computed(() => {
  const s = passwordStrength.value
  if (s <= 1) return 'strength-weak'
  if (s === 2) return 'strength-fair'
  if (s === 3) return 'strength-good'
  return 'strength-strong'
})

const passwordStrengthPercent = computed(() => passwordStrength.value * 25)

const passwordStrengthLabel = computed(() => {
  const labels = ['Weak', 'Weak', 'Fair', 'Good', 'Strong']
  return labels[passwordStrength.value] || 'Weak'
})

const mobileDigits = computed(() => {
  const digits = String(props.signupDraft.mobile ?? '').replace(/\D/g, '')
  return digits.slice(0, 10)
})

const onMobileInput = (event) => {
  const digits = String(event.target.value ?? '').replace(/\D/g, '').slice(0, 10)
  updateField('mobile', digits)
}

const updateField = (field, value) => {
  emit('update:signupDraft', {
    ...props.signupDraft,
    [field]: value
  })
}
</script>

<style scoped>
.auth-brand-icon-placeholder {
  font-size: 48px;
  color: var(--primary-color1, #23b5ee);
  display: block;
  text-align: center;
  margin-bottom: 12px;
}

.req {
  color: #dc2626;
}

.optional {
  color: #9ca3af;
  font-weight: 400;
  font-size: 12px;
}

.field-error {
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
}

.field-hint {
  color: #9ca3af;
  font-size: 12px;
  margin-top: 4px;
}

.password-hint {
  color: #9ca3af;
  font-size: 12px;
  margin-top: 4px;
}

.password-strength-bar {
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  margin-top: 8px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s, background 0.3s;
}

.strength-fill.strength-weak { background: #ef4444; }
.strength-fill.strength-fair { background: #f59e0b; }
.strength-fill.strength-good { background: #10b981; }
.strength-fill.strength-strong { background: #059669; }

.password-strength-label {
  font-size: 12px;
  margin-top: 2px;
  font-weight: 500;
}

.password-strength-label.strength-weak { color: #ef4444; }
.password-strength-label.strength-fair { color: #f59e0b; }
.password-strength-label.strength-good { color: #10b981; }
.password-strength-label.strength-strong { color: #059669; }

.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 40px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  font-size: 16px;
  line-height: 1;
}

.password-toggle:hover {
  color: #333;
}

.mobile-input-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-prefix {
  font-size: 15px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
  line-height: 48px;
  padding: 0 10px;
  background: #f9fafb;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
</style>
